import { useClassName } from '@/styles/JwVideoArticle';
import { Component, ComponentProps } from '@/types/component';
import logger from 'lib/utils/logger';
import mergeProps from 'lib/utils/mergeProps';
import { useCallback, useEffect, useState } from 'react';
import { useJWPlayer } from './JWPlayerContext';
import { VideoArticle } from './VideoArticle';
import { VideoArticleExitLinkProps } from './VideoArticle.Exit.Link';
import { VideoArticleExitMarketingTextProps } from './VideoArticle.Exit.MarketingText';
import { findB2BMediaCandidate } from './b2b/getB2BCandidate';
import { getUserInformation, saveUserInformation } from './b2b/userInformation';
import { JW_CDN_URL } from './constants';
import { PlaylistItem, Time } from './constants/playEvents';
import { usePlayerEvent } from './hooks/usePlayerEvent';
import { B2BVideoInfo } from './types/video';

export interface VideoArticleExitProps extends ComponentProps<'div'> {
  b2bVideo?: B2BVideoInfo;
  mediaId?: string;
  setShowNextVideoPopup: (show: boolean) => void;
  linkProps?: Omit<VideoArticleExitLinkProps, 'commercialLink'>;
  labelProps?: VideoArticleExitMarketingTextProps;
}

export const VideoArticleExitComponent: Component<VideoArticleExitProps> = ({
  b2bVideo,
  mediaId,
  setShowNextVideoPopup,
  linkProps,
  labelProps,
  ...props
}) => {
  const { linkText = null, markingText = null, link = null, videoCampaigns = [] } = b2bVideo ?? {};

  const className = useClassName('exit', props);
  const linkClassName = useClassName('exit_link', linkProps ?? {});
  const labelClassName = useClassName('exit_label', labelProps ?? props);

  const [commercialLinkText, setCommercialLinkText] = useState<string | null>(linkText);
  const [commercialLink, setCommercialLink] = useState<string | null>(link);
  const [commercialMarkingText, setCommercialMarkingText] = useState<string | null>(markingText);

  //To check if a decision to show b2b video has been taken
  const [didShowB2BVideo, setDidShowB2BVideo] = useState<boolean>(false);
  const [isB2BVideoPlaying, setIsB2BVideoPlaying] = useState<boolean>(false);
  const [hasB2BVideoShownThisSession, setHasB2BVideoShownThisSession] = useState<boolean>(false);

  const [b2bMediaId, setB2BMediaId] = useState<string | null>(null);
  const [b2bCampaignId, setB2BCampaignId] = useState<string | null>(null);
  const [hasUserVisitSaved, setHasUserVisitSaved] = useState<boolean>(false);

  //Read from jw player context.
  const { player, currentMedia } = useJWPlayer();
  const currentMediaId = currentMedia ? currentMedia.mediaid : mediaId;

  const fetchB2BMedia = async (mediaId: string) => {
    const data = await fetch(`${JW_CDN_URL}/media/${mediaId}`);
    const {
      playlist: [media],
    } = await data.json();

    return media;
  };

  useEffect(() => {
    const setExitLinkValues = async () => {
      setCommercialLinkText(currentMedia?.video_comm_link_text || null);
      setCommercialLink(currentMedia?.video_comm_link || null);
      setCommercialMarkingText(currentMedia?.video_comm_ad_text || null);
    };

    player?.setPlaylistItemCallback(async () => {
      if (b2bMediaId && !hasB2BVideoShownThisSession && !isB2BVideoPlaying) {
        //Hide next up popup if next video an injected b2b video
        setShowNextVideoPopup(false);
        try {
          return await fetchB2BMedia(b2bMediaId); // Play the updated media
        } catch (error) {
          logger.catch(`setPlaylistItemCallback error: `, error);
        }
      } else {
        setShowNextVideoPopup(true);
      }
    });

    setExitLinkValues();
  }, [b2bMediaId, currentMedia, hasB2BVideoShownThisSession, isB2BVideoPlaying, player, setShowNextVideoPopup]);

  const onTime = useCallback(
    async ({ position }: { position: number }) => {
      if (didShowB2BVideo) {
        return;
      }

      const resetVariables = () => {
        if (b2bCampaignId) {
          setB2BMediaId(null);
          setB2BCampaignId(null);
        }
      };

      const shouldShowB2BVideo = async () => {
        const userInfo = getUserInformation();
        const { campaignId, mediaId } = findB2BMediaCandidate(videoCampaigns, userInfo) ?? {};
        setB2BMediaId(mediaId || null);
        setB2BCampaignId(campaignId || null);
      };

      const isCurrentlyPlayingB2BVideo = b2bCampaignId && currentMediaId === b2bMediaId;
      //If a native video has not been shown in this session or one is not already playing, check if a native video should be shown.
      if (!hasB2BVideoShownThisSession && !isB2BVideoPlaying) {
        await shouldShowB2BVideo();
      } else if (!isCurrentlyPlayingB2BVideo) {
        setB2BMediaId(null);
        setB2BCampaignId(null);
      }

      const B2B_MIN_VIEW_DURATION = 3;

      if (isCurrentlyPlayingB2BVideo) {
        //If the native video has been played, set the flag to true and record the analytics.
        setHasB2BVideoShownThisSession(true);
        if (!hasUserVisitSaved) {
          setHasUserVisitSaved(true);
          await saveUserInformation(b2bCampaignId, getUserInformation());
        }
        //Record goal reached
        const hasSeenB2BVideoForExpectedDuration = position >= B2B_MIN_VIEW_DURATION;
        if (hasSeenB2BVideoForExpectedDuration) {
          resetVariables();
          setDidShowB2BVideo(true);
        }
      } else {
        setDidShowB2BVideo(true);
      }
    },
    [
      b2bCampaignId,
      videoCampaigns,
      b2bMediaId,
      currentMediaId,
      didShowB2BVideo,
      hasB2BVideoShownThisSession,
      hasUserVisitSaved,
      isB2BVideoPlaying,
    ]
  );

  const onPlaylistItem = useCallback(() => {
    //On each new video which is being played, check if it a native video and reset the flags.
    setIsB2BVideoPlaying(linkText ? true : false);
    setDidShowB2BVideo(false);
    setHasUserVisitSaved(false);
  }, []);

  usePlayerEvent(player, Time, onTime);
  usePlayerEvent(player, PlaylistItem, onPlaylistItem);
  return (
    <div {...mergeProps({ className }, props)}>
      {/* For commercial link Button */}
      {commercialLinkText && commercialLink && (
        <VideoArticle.Exit.Link
          commercialLink={commercialLink}
          {...mergeProps({ className: linkClassName }, linkProps)}
        >
          {commercialLinkText}
        </VideoArticle.Exit.Link>
      )}

      {/* For commercial ad text */}
      {commercialMarkingText && (
        <VideoArticle.Exit.MarketingText {...mergeProps({ className: labelClassName }, labelProps)}>
          {commercialMarkingText}
        </VideoArticle.Exit.MarketingText>
      )}
    </div>
  );
};
